import { useCallback, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { isOfferClonable, isOfferReadonly } from '@occc-app/offerValidationService/utils';
import { Container, SwitchChainAlert, snackbar } from '@occc-app/design-system';
import { Offer_Engagement_Type } from '@occc/graphql';
import { useCopyCampaignComponent } from '@occc-app/offerHelpers/hooks/useCopyCampaignComponent';
import { getOfferComponentType } from '@occc-app/offerHelpers/utils';
import { createRouteCreator } from '@occc-app/routes/CampaignComponents/utils';
import { routeCreator as campaignRouteCreator } from '@occc-app/routes/Campaign/utils';
import config from '@occc/config';
import { useOfferDeleteMutation } from '@occc-app/dataAccessLayer/offer/mutations';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { usePreparedCampaignOfferHook } from '@occc-app/routes/CampaignComponents/component-details/use-prepared-offer.hook';
import { ComponentDetailsPage } from './component-details.page';
import { CampaignComponentCopyDialogFormValues } from '../components/CampaignComponentCopyDialog/types';

export const ComponentDetailsPageContainer = () => {
  const navigate = useNavigate();
  const copyCampaignComponent = useCopyCampaignComponent();

  const { id: campaignId = '', componentId: offerId = '' } = useParams<{
    id: string;
    componentId: string;
  }>();

  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState<boolean>(false);
  const [isCopyInProgress, setIsCopyInProgress] = useState<boolean>(false);

  const isUserPermittedToDeleteCampaignComponent = useIsUserPermittedTo(
    Feature.CAMPAIGN_COMPONENT_DELETE,
  );

  const [offerDelete, { loading: isArchiveInProgress }] = useOfferDeleteMutation({
    onCompleted: () => {
      snackbar.success('Offer has been archived.');
      navigate(campaignRouteCreator.details(campaignId));
    },
    refetchQueries: ['campaignComponents'],
  });

  const {
    offer,
    campaign,
    isComponentFromDifferentChain,
    constraintSupplementalData,
    error,
    loading,
  } = usePreparedCampaignOfferHook(offerId, campaignId);

  const openCopyDialog = useCallback(() => {
    setIsCopyDialogOpen(true);
  }, []);

  const closeCopyDialog = useCallback(() => {
    setIsCopyDialogOpen(false);
  }, []);

  const handleOfferArchive = useCallback(() => {
    offerDelete({
      variables: { id: offerId },
    });
  }, [offerDelete, offerId]);

  const handleOfferCopy = useCallback(
    async (values: CampaignComponentCopyDialogFormValues) => {
      if (!offer) {
        return;
      }

      setIsCopyInProgress(true);

      const campaignUuid = values.campaign.uuid;
      const offerId = await copyCampaignComponent({
        offer,
        campaignUuid,
        constraints: offer?.preparedConstraints,
      });
      const offerType = getOfferComponentType(offer);

      setIsCopyInProgress(false);
      setIsCopyDialogOpen(false);

      if (offerId && offerType) {
        const routeCreator = createRouteCreator(offerType);
        navigate(routeCreator.updateWithTab(campaignUuid, offerId));
      }
    },
    [copyCampaignComponent, navigate, offer],
  );

  if (isComponentFromDifferentChain) {
    return (
      <Container maxWidth='1024px'>
        <SwitchChainAlert
          title='This component is not available in your currently selected chain.'
          requiredChainId={campaign?.chain.id || ''}
        />
      </Container>
    );
  }

  if (
    offer?.engagementType === Offer_Engagement_Type.Promocode &&
    !config.features.promocodesOffersEnabled
  ) {
    return <Navigate to='/' replace />;
  }

  return (
    <Container>
      <ComponentDetailsPage
        campaign={campaign}
        offer={offer}
        constraintSupplementalData={constraintSupplementalData}
        loading={loading}
        initiallyLoading={loading && (!offer || !campaign)}
        isEditAvailable={!isOfferReadonly(offer)}
        isCopyAvailable={isOfferClonable(offer)}
        isCopyInProgress={isCopyInProgress}
        onCopyDialogOpen={openCopyDialog}
        onCopyDialogClose={closeCopyDialog}
        isCopyDialogOpen={isCopyDialogOpen}
        error={error}
        onOfferCopy={handleOfferCopy}
        isArchiveAvailable={isUserPermittedToDeleteCampaignComponent && !offer?.deleted}
        isArchiveInProgress={isArchiveInProgress}
        onOfferArchive={handleOfferArchive}
      />
    </Container>
  );
};
